/**
 * Hook pour gérer la maintenance de la base de données
 * @module admin/hooks/useDatabaseMaintenance
 */

import { useState, useCallback } from 'react';
import {
  getDatabaseStats,
  cleanupDatabase,
} from '../services/maintenanceService';
import { DEFAULT_CLEANUP_OPTIONS } from '../constants/defaultValues';

/**
 * Hook de gestion de la maintenance de la base de données
 * @returns {Object} État et fonctions pour la maintenance
 */
export const useDatabaseMaintenance = () => {
  const [dbStats, setDbStats] = useState(null);
  const [cleanupOptions, setCleanupOptions] = useState(DEFAULT_CLEANUP_OPTIONS);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const [maintenanceError, setMaintenanceError] = useState(null);

  /**
   * Récupère les statistiques de la base de données
   */
  const fetchDbStats = useCallback(async () => {
    try {
      const data = await getDatabaseStats();
      setDbStats(data.data);
    } catch (err) {
      console.error('Erreur lors de la récupération des statistiques BD:', err);
    }
  }, []);

  /**
   * Nettoie la base de données
   * @param {boolean} dryRun - Si true, simule le nettoyage
   */
  const handleDatabaseCleanup = useCallback(async (dryRun = false) => {
    setMaintenanceLoading(true);
    setMaintenanceResult(null);
    setMaintenanceError(null);

    try {
      const data = await cleanupDatabase(cleanupOptions, dryRun);
      setMaintenanceResult(data);

      if (!dryRun) {
        await fetchDbStats();
      }
    } catch (err) {
      setMaintenanceError(err.message);
    } finally {
      setMaintenanceLoading(false);
    }
  }, [cleanupOptions, fetchDbStats]);

  /**
   * Gère le changement d'une option de nettoyage
   * @param {string} field - Nom du champ
   * @param {*} value - Nouvelle valeur
   */
  const handleCleanupOptionChange = useCallback((field, value) => {
    if (field === 'tables') {
      setCleanupOptions((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      setCleanupOptions((prev) => ({
        ...prev,
        [field]: parseInt(value, 10),
      }));
    }
  }, []);

  return {
    dbStats,
    cleanupOptions,
    maintenanceLoading,
    maintenanceResult,
    maintenanceError,
    fetchDbStats,
    handleDatabaseCleanup,
    handleCleanupOptionChange,
  };
};
